// Which board this tab shows (docs/design/orgs.md 4.1). The board is a query parameter, so each tab
// (or each side of Chrome's split view) keeps its own: `?org=gautam`. No ?org=, or ?org=oemuap, is
// OEMUAP, the company board, as every link from before means. Pure, apart from reading the URL and
// the localStorage helpers (each wrapped: a private window may refuse them).

/** OEMUAP's slug: what no ?org= means. */
export const COMPANY_SLUG = "oemuap";

/** The board a query string names: its slug, lowercased; OEMUAP when it names none. Pure. */
export function orgFromSearch(search: string): string {
  const v = new URLSearchParams(search).get("org")?.trim().toLowerCase();
  return v ? v : COMPANY_SLUG;
}

/** Did the URL name a board (even OEMUAP)? The old paths serve OEMUAP when it didn't. Pure. */
export const namesOrg = (search: string) => !!new URLSearchParams(search).get("org")?.trim();

/**
 * Where this tab's board API lives: the old /api paths for a URL without ?org= (OEMUAP, exactly as
 * before), /api/orgs/<slug> when the URL names a board (?org=oemuap included). Pure.
 */
export function apiBaseFor(search: string): string {
  return namesOrg(search) ? `/api/orgs/${encodeURIComponent(orgFromSearch(search))}` : "/api";
}

/** This tab's board slug and API base (the URL's ?org= doesn't change while the page is open). */
const search = () => (typeof window === "undefined" ? "" : window.location.search);
export const currentOrg = () => orgFromSearch(search());
export const apiBase = () => apiBaseFor(search());

/** The project board a URL names: &project=01 is project 01 on this tab's board. Pure. */
export function projectFromSearch(search: string): number | null {
  const v = new URLSearchParams(search).get("project")?.trim();
  if (!v || !/^\d{1,4}$/.test(v)) return null;
  const n = Number(v);
  return n > 0 ? n : null;
}

/** The query string with &project= set to this project number (two digits, as people write it), or taken out. Pure. */
export function withProject(search: string, projectNo: number | null): string {
  const q = new URLSearchParams(search);
  if (projectNo == null) q.delete("project");
  else q.set("project", String(projectNo).padStart(2, "0"));
  // (?panels= stays readable, as panels.ts writes it)
  const s = q.toString().replace(/%3A/gi, ":").replace(/%2C/gi, ",");
  return s ? `?${s}` : "";
}

/** A localStorage key for this board: settings per board, so two tabs on two boards don't share them. Pure. */
export const orgKey = (base: string, org: string) => `${base}.${org}`;

/**
 * Read a per-board setting. OEMUAP falls back once to the key from before boards existed
 * (`buyoem.project` becomes `buyoem.project.oemuap`), so nobody loses their place.
 */
export function readOrgSetting(base: string, org = currentOrg()): string | null {
  try {
    const v = localStorage.getItem(orgKey(base, org));
    if (v !== null || org !== COMPANY_SLUG) return v;
    const old = localStorage.getItem(base);
    if (old !== null) {
      localStorage.setItem(orgKey(base, org), old);
      localStorage.removeItem(base);
    }
    return old;
  } catch {
    return null;
  }
}

/** Save (or with null, remove) a per-board setting. */
export function writeOrgSetting(base: string, value: string | null, org = currentOrg()) {
  try {
    if (value === null) localStorage.removeItem(orgKey(base, org));
    else localStorage.setItem(orgKey(base, org), value);
  } catch {
    /* a per-board convenience only */
  }
}
